import { Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@fleet/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { cn } from "@vigia/shared";

// Purposefully generic: `CounterBadge` is a UI atom with no domain of its own (it
// is used by `SidebarNavItem` for ANY counter), so the fallback cannot
// presuppose a product noun (e.g. "vehicles", "alerts") that
// no caller asks for yet. "items" is honest about what the component
// actually knows: a quantity, nothing more.
const DEFAULT_LABEL: MessageDescriptor = msg`items`;

interface CounterBadgeProps {
  count: number | undefined;
  class?: string;
  label?: MessageDescriptor;
  tooltipDisabled?: boolean;
  showZero?: boolean;
}

export function CounterBadge(props: CounterBadgeProps) {
  const { locale, t } = useI18n();

  const isCompact = (): boolean => props.count !== undefined && props.count >= COMPACT_THRESHOLD;

  const formattedCount = (): string =>
    props.count === undefined ? "" : formatCompactNumber(props.count, locale());

  // Localized description for accessibility and tooltips
  const fullLabel = (): string => {
    if (props.count === undefined) {
      return "";
    }
    return `${props.count.toLocaleString(locale())} ${t(props.label ?? DEFAULT_LABEL)}`;
  };

  return (
    <Show
      when={props.count !== undefined && (props.count > 0 || (props.showZero && props.count === 0))}
    >
      <Show
        when={isCompact() && !props.tooltipDisabled}
        fallback={
          <span
            class={cn(
              "inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-muted-foreground/10 text-muted-foreground transition-all text-[10px] font-bold select-none animate-fade-in",
              props.class,
            )}
            aria-label={fullLabel()}
          >
            {props.count}
          </span>
        }
      >
        <Tooltip openDelay={350} closeDelay={100}>
          <TooltipTrigger class="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent rounded-full select-none cursor-help">
            <span
              class={cn(
                "inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-muted-foreground/10 text-muted-foreground hover:bg-accent/15 hover:text-accent transition-all text-[10px] font-bold animate-fade-in",
                props.class,
              )}
              aria-label={fullLabel()}
            >
              {formattedCount()}
            </span>
          </TooltipTrigger>
          <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
            {fullLabel()}
          </TooltipContent>
        </Tooltip>
      </Show>
    </Show>
  );
}
